import React, { useEffect, useState } from 'react';
import { Button, Group, Text, Badge, Box } from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { formatDate, formatDateTime } from '../../utils/DateUtil.ts';
import { ParkingBilling } from '../../models/parkingBilling.ts';
import useDownloadBillingReport from '../../hooks/useDownloadBillingReport.tsx';
import { notifications } from '@mantine/notifications';
import sortBy from 'lodash.sortby';

type HistoryBillingTableProps = {
  data: ParkingBilling[];
  isLoading: boolean;
};

const PAGE_SIZE = 10;

export default function HistoryBillingTable({
  data = [],
  isLoading,
}: HistoryBillingTableProps) {
  const { mutate: downloadBillReport } = useDownloadBillingReport();
  const [records, setRecords] = useState(
    sortBy(data, 'readyDateTime').reverse().slice(0, PAGE_SIZE),
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE;
    setRecords(sortBy(data, 'readyDateTime').reverse().slice(from, to));
  }, [data, page]);

  const columns: DataTableColumn<ParkingBilling>[] = [
    {
      title: '對帳單/廠商',
      accessor: 'name',
      render: ({ name, siteProvider }) => (
        <div>
          <Text size="sm">{name}</Text>
          <Text size="sm" c="dimmed">
            {siteProvider.name}
          </Text>
        </div>
      ),
    },
    {
      title: '對帳期間',
      accessor: 'period',
      render: ({ billDateTimeFrom, billDateTimeTo }) => {
        return `${formatDate(billDateTimeFrom)} ~ ${formatDate(billDateTimeTo)}`;
      },
    },
    {
      title: '匯款金額',
      accessor: 'totalRemittanceAmount',
      render: ({ summary }) => summary.totalRemittanceAmount.toLocaleString(),
    },
    {
      title: '手續費',
      accessor: 'totalFeeAmount',
      render: ({ summary }) => summary.totalFeeAmount.toLocaleString(),
    },
    {
      title: '廠商開立發票金額',
      accessor: 'inputInvoiceAmount',
      render: ({ summary }) => {
        return summary.inputInvoiceAmount.toLocaleString();
      },
    },
    {
      title: '產檔時間',
      accessor: 'readyDateTime',
      render: ({ readyDateTime }) => {
        return formatDateTime(readyDateTime);
      },
    },
    {
      title: '標籤',
      accessor: 'tags',
      render: ({ tags }) => (
        <Group>
          {tags?.map((tag) => (
            <Badge key={tag.id} variant="outline">
              {tag.name}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      title: '操作',
      accessor: 'actions',
      render: (row) => (
        <Button
          size="xs"
          variant="outline"
          onClick={() => {
            downloadBillReport(row.id, {
              onSuccess: () => {
                notifications.show({
                  title: '下載成功',
                  message: `對帳單 ${row.name} 的報表已下載`,
                  color: 'green',
                });
              },
            });
          }}
        >
          下載報表
        </Button>
      ),
    },
  ];
  return (
    <Box>
      <DataTable
        idAccessor="id"
        highlightOnHover
        minHeight={150}
        columns={columns}
        records={records}
        verticalSpacing="xs"
        fetching={isLoading}
        noRecordsText="沒有歷史對帳單"
        recordsPerPage={PAGE_SIZE}
        totalRecords={data.length}
        page={page}
        onPageChange={setPage}
      />
    </Box>
  );
}
